import React, { useMemo, useState } from "react";
import { FaMagnifyingGlass } from "react-icons/fa6";
import styles from "../../styles/evento-detalle.module.css";

export function MultiSelectChips({
    options,
    value = [],
    onChange,
    placeholder = "Buscar...",
    labelKey = "label",
    valueKey = "value",
    slotLabels = [],
    hideSelected = false,
}) {
    const [query, setQuery] = useState("");
    const [dragIndex, setDragIndex] = useState(null);
    const selectedSet = useMemo(() => new Set(value), [value]);

    const moveItem = (from, to) => {
        if (from === to || from == null || to == null) return;
        const next = [...value];
        const [m] = next.splice(from, 1);
        next.splice(to, 0, m);
        onChange?.(next);
    };

    const onDragStart = (i) => setDragIndex(i);
    const onDragOver = (e) => e.preventDefault();
    const onDrop = (i) => {
        moveItem(dragIndex, i);
        setDragIndex(null);
    };

    const filtered = useMemo(() => {
        const q = query.toLowerCase().trim();
        if (!q) return options;
        return options.filter((o) => String(o[labelKey]).toLowerCase().includes(q));
    }, [options, query, labelKey]);

    const toggle = (v) => {
        const exists = selectedSet.has(v);
        if (exists) {
            onChange?.(value.filter((x) => x !== v));
            return;
        }

        // OJO: este límite de 3 solo tiene sentido para platos del menú.
        // Si lo vas a reutilizar para agentes/otras selecciones, conviene parametrizarlo.
        if (value.length >= 3) {
            alert("Solo puedes elegir 3 platos para el menú.");
            return;
        }

        onChange?.([...value, v]);
    };

    const clearAll = () => onChange?.([]);

    return (
        <div className={styles.ms}>
            <div className={styles["ms-input"]}>
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={placeholder}
                    aria-label="Buscar elementos"
                />
                {value.length > 0 && (
                    <button
                        type="button"
                        className={`${styles.btn} ${styles.ghost} ${styles.xs}`}
                        onClick={clearAll}
                        aria-label="Limpiar selección"
                    >
                        Limpiar
                    </button>
                )}
            </div>

            {!hideSelected && value.length > 0 && (
                <div className={styles.chips}>
                    {value.map((v, idx) => {
                        const opt = options.find((o) => o[valueKey] === v);
                        return (
                            <span
                                key={v}
                                className={`${styles.chip} ${styles.draggable}`}
                                draggable
                                onDragStart={() => onDragStart(idx)}
                                onDragOver={onDragOver}
                                onDrop={() => onDrop(idx)}
                            >
                                {slotLabels[idx] && (
                                    <span className={styles.slotBadge}>{slotLabels[idx]}</span>
                                )}
                                {opt ? opt[labelKey] : v}
                            </span>
                        );
                    })}
                </div>
            )}

            <div className={styles["ms-list"]} role="listbox" aria-label="Opciones disponibles">
                {filtered.length === 0 ? (
                    <div className={styles.empty}>
                        <FaMagnifyingGlass style={{ fontSize: "30px" }} />
                    </div>
                ) : (
                    filtered.map((o) => {
                        const v = o[valueKey];
                        const selected = selectedSet.has(v);
                        return (
                            <button
                                key={v}
                                type="button"
                                className={`${styles["ms-row"]} ${selected ? styles.selected : ""}`}
                                onClick={() => toggle(v)}
                                role="option"
                                aria-selected={selected}
                                title={o[labelKey]}
                            >
                                <input type="checkbox" readOnly checked={selected} />
                                <span>{o[labelKey]}</span>
                            </button>
                        );
                    })
                )}
            </div>
        </div>
    );
}
